
import type { ChatMessage, SessionMemory, MemoryUpdate } from '@/types/ai';

const memoryInstances = new Map<string, SessionMemoryManager>();

export function getMemoryManager(sessionId: string, userId?: string): SessionMemoryManager {
  const key = `${sessionId}_${userId || 'anonymous'}`;
  
  if (!memoryInstances.has(key)) {
    const manager = new SessionMemoryManager(sessionId, userId);
    memoryInstances.set(key, manager);
  }
  
  return memoryInstances.get(key)!;
}

export class SessionMemoryManager {
  private memory: SessionMemory;
  private updateQueue: MemoryUpdate[] = [];
  private processingQueue = false;
  
  constructor(sessionId?: string, userId?: string) {
    this.memory = {
      sessionId: sessionId || this.generateSessionId(),
      userId,
      messages: [],
      summary: '',
      context: {
        city: '',
        selectedPills: [],
        viewedVenues: [],
        savedVenues: [],
        neighborhoods: [],
        lastActivity: Date.now()
      },
      lastSummarizedAt: 0,
      tokenCount: 0,
      messagesSinceSummary: 0
    };
    
    this.loadFromStorage();
  }

  setCity(city: string): void {
    const safeCity = (city || '').trim();
    if (safeCity && safeCity !== this.memory.context.city) {
      this.memory.context.city = safeCity;
      this.memory.context.lastActivity = Date.now();
      this.saveToStorage();
    }
  }
  
  addMessage(message: ChatMessage): void {
    if (!message.content) {
      message.content = '';
    }
    
    this.memory.messages.push({
      ...message,
      id: message.id || this.generateId(),
      timestamp: message.timestamp || Date.now()
    });
    
    this.memory.messagesSinceSummary++;
    this.memory.tokenCount += this.estimateTokens(message.content);
    this.memory.context.lastActivity = Date.now();
    
    this.trimMemory();
    
    this.saveToStorage();
  }
  
  trackVenue(venueId: string, action: 'view' | 'save' | 'unsave'): void {
    if (!venueId) return;
    
    switch (action) {
      case 'view':
        if (!this.memory.context.viewedVenues.includes(venueId)) {
          this.memory.context.viewedVenues.push(venueId);
          if (this.memory.context.viewedVenues.length > 50) {
            this.memory.context.viewedVenues.shift();
          }
        }
        break;
        
      case 'save':
        if (!this.memory.context.savedVenues.includes(venueId)) {
          this.memory.context.savedVenues.push(venueId);
        }
        break;
        
      case 'unsave':
        this.memory.context.savedVenues = this.memory.context.savedVenues.filter(
          id => id !== venueId
        );
        break;
    }
    
    this.memory.context.lastActivity = Date.now();
    this.saveToStorage();
  }
  
  updateContext(update: Partial<SessionMemory['context']>): void {
    this.memory.context = {
      ...this.memory.context,
      ...update,
      lastActivity: Date.now()
    };
    this.saveToStorage();
  }
  
  getMemory(): SessionMemory {
    return { ...this.memory };
  }
  
  getMessagesForAPI(limit: number = 10): ChatMessage[] {
    return this.memory.messages.slice(-limit);
  }
  
  getFormattedContext(): string {
    const recentMessages = this.getMessagesForAPI(5);
    const context = {
      summary: this.memory.summary,
      recentMessages: recentMessages.map(m => `${m.role}: ${m.content}`).join('\n'),
      city: this.memory.context.city,
      interests: this.memory.context.selectedPills.join(', '),
      viewedVenues: this.memory.context.viewedVenues.slice(-10),
      savedVenues: this.memory.context.savedVenues
    };
    
    return JSON.stringify(context, null, 2);
  }
  
  needsSummarization(): boolean {
    return (
      this.memory.messagesSinceSummary >= 10 ||
      this.memory.tokenCount > 2000
    );
  }
  
  updateSummary(summary: string): void {
    this.memory.summary = summary;
    this.memory.lastSummarizedAt = Date.now();
    this.memory.messagesSinceSummary = 0;
    
    if (this.memory.messages.length > 20) {
      this.memory.messages = this.memory.messages.slice(-10);
      this.recalculateTokenCount();
    }
    
    this.saveToStorage();
  }
  
  clear(): void {
    this.memory.messages = [];
    this.memory.summary = '';
    this.memory.tokenCount = 0;
    this.memory.messagesSinceSummary = 0;
    this.memory.context.viewedVenues = [];
    this.memory.context.savedVenues = [];
    this.memory.context.selectedPills = [];
    
    this.saveToStorage();
  }
  
  private trimMemory(): void {
    const maxMessages = 50;
    const maxTokens = 4000;
    
    if (this.memory.messages.length > maxMessages) {
      const toRemove = this.memory.messages.length - maxMessages;
      this.memory.messages = this.memory.messages.slice(toRemove);
    }
    
    while (this.memory.tokenCount > maxTokens && this.memory.messages.length > 1) {
      const removed = this.memory.messages.shift();
      if (removed) {
        this.memory.tokenCount -= this.estimateTokens(removed.content || '');
      }
    }
  }
  
  private estimateTokens(text: string): number {
    if (!text || typeof text !== 'string') {
      return 0;
    }
    return Math.ceil(text.length / 4);
  }
  
  private recalculateTokenCount(): void {
    this.memory.tokenCount = this.memory.messages.reduce(
      (total, msg) => total + this.estimateTokens(msg.content || ''),
      0
    );
  }
  
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }
  
  private generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }
  
  private saveToStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const key = `remy_memory_${this.memory.sessionId}`;
      const data = JSON.stringify(this.memory);
      sessionStorage.setItem(key, data);
    } catch (error) {
      console.warn('[Memory] Failed to save to storage:', error);
    }
  }
  
  private loadFromStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      const key = `remy_memory_${this.memory.sessionId}`;
      const data = sessionStorage.getItem(key);
      
      if (data) {
        const loaded = JSON.parse(data) as SessionMemory;
        this.memory = {
          ...loaded,
          sessionId: this.memory.sessionId,
          userId: this.memory.userId || loaded.userId
        };
      }
    } catch (error) {
      console.warn('[Memory] Failed to load from storage:', error);
    }
  }
}

export function formatMessagesForDisplay(messages: ChatMessage[]): string {
  return messages
    .map(msg => `${msg.role === 'user' ? '👤' : '🤖'} ${msg.content}`)
    .join('\n\n');
}

export function extractVenueIds(messages: ChatMessage[]): string[] {
  const venueIds = new Set<string>();
  
  messages.forEach(msg => {
    if (msg.metadata?.venuesViewed) {
      msg.metadata.venuesViewed.forEach(id => venueIds.add(id));
    }
    if (msg.metadata?.venuesSaved) {
      msg.metadata.venuesSaved.forEach(id => venueIds.add(id));
    }
  });
  
  return Array.from(venueIds);
}

export function calculateStats(memory: SessionMemory) {
  const sessionDuration = memory.context.lastActivity 
    ? Date.now() - (memory.context.lastActivity - (1000 * 60 * 60)) // Approximate session start
    : 0;
  
  return {
    totalMessages: memory.messages.length,
    totalTokens: memory.tokenCount,
    summarizationCount: memory.lastSummarizedAt ? 1 : 0,
    lastSummarizedAt: memory.lastSummarizedAt,
    venuesViewed: memory.context.viewedVenues.length,
    venuesSaved: memory.context.savedVenues.length,
    sessionDuration: Math.round(sessionDuration / 1000 / 60) // in minutes
  };
}
